/**
 * Fetch and mutate property tags (many-to-many). Tags are case-insensitive.
 * Batched: one query for all propertyIds (no N+1).
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const QUERY_KEY = 'property-tags';

async function fetchTags(propertyIds: string[]): Promise<Map<string, string[]>> {
  if (propertyIds.length === 0) return new Map();
  const { data, error } = await supabase
    .from('property_tags')
    .select('property_id, tag')
    .in('property_id', propertyIds)
    .order('tag');
  if (error) throw error;
  const map = new Map<string, string[]>();
  for (const row of data ?? []) {
    const id = row.property_id;
    const list = map.get(id) ?? [];
    list.push(row.tag);
    map.set(id, list);
  }
  return map;
}

export function usePropertyTags(propertyIds: string[]) {
  const key = [...propertyIds].sort().join(',');
  const query = useQuery({
    queryKey: [QUERY_KEY, key],
    queryFn: () => fetchTags(propertyIds),
    enabled: propertyIds.length > 0,
  });
  return {
    tagsByPropertyId: query.data ?? new Map<string, string[]>(),
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}

/** Normalize tag: trim and lower for storage (DB constraint is lower(trim(tag))) */
function normalizeTag(tag: string): string {
  return tag.trim().toLowerCase();
}

export function usePropertyTagsMutation(propertyId: string) {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (tags: string[]) => {
      const normalized = [...new Set(tags.map(normalizeTag).filter(Boolean))];
      const { error: delErr } = await supabase
        .from('property_tags')
        .delete()
        .eq('property_id', propertyId);
      if (delErr) throw delErr;
      if (normalized.length === 0) return;
      const { error: insErr } = await supabase.from('property_tags').insert(
        normalized.map((tag) => ({ property_id: propertyId, tag }))
      );
      if (insErr) throw insErr;
    },
    onSuccess: (_, __, ctx) => {
      client.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
  return mutation;
}

export function useAddPropertyTag(propertyId: string) {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (tag: string) => {
      const t = normalizeTag(tag);
      if (!t) return;
      const { error } = await supabase
        .from('property_tags')
        .upsert({ property_id: propertyId, tag: t }, { onConflict: 'property_id,tag' });
      if (error) throw error;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
  return mutation;
}

export function useRemovePropertyTag(propertyId: string) {
  const client = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (tag: string) => {
      const { error } = await supabase
        .from('property_tags')
        .delete()
        .eq('property_id', propertyId)
        .eq('tag', normalizeTag(tag));
      if (error) throw error;
    },
    onSuccess: () => {
      client.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
  return mutation;
}
